import { CookieOptions, StorageProvider } from './types';

/**
 * CookieStorage provider implementation
 */
export class CookieStorageProvider implements StorageProvider {
  /**
   * Get a value from cookies
   * @param key The key to retrieve
   * @returns The stored value or null if not found
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const cookieValue = this.getCookieValue(key);
      return cookieValue ? JSON.parse(decodeURIComponent(cookieValue)) as T : null;
    } catch (error) {
      console.error('Error retrieving from cookies:', error);
      return null;
    }
  }

  /**
   * Store a value in cookies
   * @param key The key to store the value under
   * @param value The value to store
   * @param options Cookie options
   */
  async put<T>(key: string, value: T, options: CookieOptions = {}): Promise<void> {
    try {
      const serializedValue = encodeURIComponent(JSON.stringify(value));
      let cookieString = `${key}=${serializedValue}`;
      
      if (options.path) cookieString += `; path=${options.path}`;
      if (options.domain) cookieString += `; domain=${options.domain}`;
      
      if (options.expires) {
        const expiresValue = typeof options.expires === 'number' 
          ? new Date(Date.now() + options.expires).toUTCString()
          : options.expires.toUTCString();
        cookieString += `; expires=${expiresValue}`;
      } else if (options.maxAge) {
        cookieString += `; max-age=${options.maxAge}`;
      }
      
      if (options.secure) cookieString += '; secure';
      if (options.sameSite) cookieString += `; samesite=${options.sameSite}`;
      if (options.httpOnly) cookieString += '; HttpOnly';
      
      document.cookie = cookieString;
    } catch (error) {
      console.error('Error storing in cookies:', error);
      throw error;
    }
  }

  /**
   * Remove a cookie
   * @param key The key to remove
   * @param options Cookie options (path and domain must match the cookie to be removed)
   */
  async remove(key: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): Promise<void> {
    try {
      // Set expiration to past date to remove the cookie
      const expiredOptions: CookieOptions = {
        ...options,
        expires: new Date(0)
      };
      
      await this.put(key, '', expiredOptions);
    } catch (error) {
      console.error('Error removing cookie:', error);
      throw error;
    }
  }

  /**
   * Check if a cookie exists
   * @param key The key to check
   * @returns True if the cookie exists, false otherwise
   */
  async has(key: string): Promise<boolean> {
    return this.getCookieValue(key) !== null;
  }
  
  /**
   * Get the storage type name
   */
  getStorageType(): string {
    return 'cookie';
  }

  /**
   * Helper method to get a cookie value by name
   * @param name The cookie name
   * @returns The cookie value or null if not found
   */
  private getCookieValue(name: string): string | null {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  }
}